import { Button, ButtonGroup, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DateRangePicker as DateRangeNew } from 'app/ComponentLibrary/DateRangeNew';

import { getFirstAndLastDateOfMonth, getSaturdayAndSundayOfGivenDate } from 'app/helpers/dateHelper';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import { addDays } from 'date-fns';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

enum CustomTimeframe {
  Today = 'Today',
  Week = 'This Week',
  Month = 'This Month',
}

enum RangePickerAction {
  onChange,
  apply,
  cancel,
}

const useStyles = makeStyles()(theme => ({
  title: {
    textAlign: 'left',
    font: 'normal normal normal 18px/12px Roboto',
    letterSpacing: '0px',
    color: '#333333',
    opacity: 1,
    marginTop: '20px',
  },
  selectedTime: {
    background: theme.palette.components.button.secondary.backgroundColor,
    color: '#fff',
    textTransform: 'none',
    '&:hover': {
      background: theme.palette.components.button.secondary.backgroundColor,
      color: '#fff',
    },
  },
  unSelectedTime: {
    background: '#fff',
    color: '#555555',
    textTransform: 'none',
    '&:hover': {
      background: '#bbdefb',
      color: '#fff',
    },
  },
  divider: {
    height: '2px',
    backgroundColor: '#F5F5F5',
    width: '99%',
  },
}));

export default function Timeframe({ onDateChange }) {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: new Date(new Date().setHours(0, 0, 0, 0)),
    endDate: new Date(new Date().setHours(23, 59, 59, 999)),
  });

  const dateRangePreviousRef = useRef(dateRange);

  const [customRange, setCustomRange] = useState<CustomTimeframe | null>(CustomTimeframe.Today);
  const [title, setTitle] = useState<string>(CustomTimeframe.Today);

  const handleDateRange = (selectedRange, action?: RangePickerAction) => {
    if (action === RangePickerAction.onChange) {
      //Hold Preious date range picker value
      dateRangePreviousRef.current = { startDate: dateRange.startDate, endDate: dateRange.endDate };
    }

    if (action === RangePickerAction.apply) {
      if (
        moment(selectedRange.startDate).format('MMMM Do, YYYY') ===
        moment(selectedRange.endDate).format('MMMM Do, YYYY')
      ) {
        setTitle(CustomTimeframe.Today);
        setCustomRange(CustomTimeframe.Today);
      } else {
        setTitle(
          `${moment(selectedRange.startDate).format('MMMM Do, YYYY')} - ${moment(selectedRange.endDate).format(
            'MMMM Do, YYYY',
          )} `,
        );
        setCustomRange(null);
      }
      setDateRange({ startDate: selectedRange.startDate, endDate: selectedRange.endDate });
      onDateChange({ startDate: selectedRange.startDate, endDate: selectedRange.endDate });
    }
    if (action === RangePickerAction.cancel) {
      setDateRange({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      onDateChange({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      setOpenDatePicker(false);
    }
  };

  const handleCustomRange = (range: CustomTimeframe) => {
    setCustomRange(range);
    setTitle(`${range}`);
    const selectedRange = {
      startDate:
        range === CustomTimeframe.Week
          ? getSaturdayAndSundayOfGivenDate(new Date()).sunday
          : range === CustomTimeframe.Month
          ? getFirstAndLastDateOfMonth(new Date().getFullYear(), new Date().getMonth()).firstDay
          : new Date(),
      endDate:
        range === CustomTimeframe.Week
          ? getSaturdayAndSundayOfGivenDate(new Date()).saturday
          : range === CustomTimeframe.Month
          ? getFirstAndLastDateOfMonth(new Date().getFullYear(), new Date().getMonth()).lastDay
          : new Date(),
    };
    setDateRange(selectedRange);
    onDateChange(selectedRange);
  };

  return (
    <>
      <LayoutGrid justifyContent={'space-between'}>
        <LayoutGridItem>
          <Typography variant="h6" className={classes.title}>
            {title === CustomTimeframe.Week
              ? `${title}, ${moment().startOf('week').format('MMMM Do, YYYY')} - ${moment()
                  .endOf('week')
                  .format('MMMM Do, YYYY')}`
              : title === CustomTimeframe.Month
              ? `${title}, ${moment(
                  getFirstAndLastDateOfMonth(new Date().getFullYear(), new Date().getMonth()).firstDay,
                ).format('MMMM Do, YYYY')} - ${moment(
                  getFirstAndLastDateOfMonth(new Date().getFullYear(), new Date().getMonth()).lastDay,
                ).format('MMMM Do, YYYY')}`
              : title === CustomTimeframe.Today
              ? `Today, ${moment().format('MMMM Do, YYYY')}`
              : `Custom Range, ${moment(dateRange.startDate).format('MMMM Do, YYYY')} - ${moment(
                  dateRange.endDate,
                ).format('MMMM Do, YYYY')}`}
          </Typography>
        </LayoutGridItem>
        <LayoutGridItem xs={6} display={'flex'} ml={2}>
          <LayoutGridItem xs={6}>
            <DateRangeNew
              placeholder={t('Custom Date Range')}
              onChange={val => handleDateRange(val, RangePickerAction.onChange)}
              onApply={val => handleDateRange(val, RangePickerAction.apply)}
              onCancel={() => handleDateRange(null, RangePickerAction.cancel)}
              value={dateRange}
              open={openDatePicker}
              actionBar
              isDateIconReq={true}
              fullWidth
              maxWidth={500}
              initialDateRange={{
                startDate: new Date(dateRange.startDate),
                endDate: new Date(dateRange.endDate),
                label:
                  moment(dateRange.startDate).format('MM/DD/YYYY') === moment(dateRange.endDate).format('MM/DD/YYYY')
                    ? `${moment(dateRange.startDate).format('MM/DD/YYYY')}`
                    : `${moment(dateRange.startDate).format('MM/DD/YYYY')} - ${moment(dateRange.endDate).format(
                        'MM/DD/YYYY',
                      )}`,
                useLabelAsValue: true,
              }}
              definedRanges={[
                {
                  label: 'Last 30 days',
                  startDate: addDays(new Date(), -30),
                  endDate: new Date(),
                  useLabelAsValue: true,
                },
                {
                  label: 'Last 60 days',
                  startDate: addDays(new Date(), -60),
                  endDate: new Date(),
                  useLabelAsValue: true,
                },
                {
                  label: 'Last 90 days',
                  startDate: addDays(new Date(), -90),
                  endDate: new Date(),
                  useLabelAsValue: true,
                },
              ]}
            />
          </LayoutGridItem>
          <LayoutGridItem xs={6} ml={2}>
            <ButtonGroup variant="contained" fullWidth={true} sx={{ height: ' 100%' }}>
              <Button
                disableRipple
                className={customRange === CustomTimeframe.Today ? classes.selectedTime : classes.unSelectedTime}
                onClick={event => handleCustomRange(CustomTimeframe.Today)}
                sx={{ height: ' 100%' }}
              >
                {CustomTimeframe.Today}
              </Button>
              <Button
                disableRipple
                className={customRange === CustomTimeframe.Week ? classes.selectedTime : classes.unSelectedTime}
                onClick={event => handleCustomRange(CustomTimeframe.Week)}
                sx={{ height: ' 100%' }}
              >
                {CustomTimeframe.Week}
              </Button>
              <Button
                disableRipple
                className={customRange === CustomTimeframe.Month ? classes.selectedTime : classes.unSelectedTime}
                onClick={event => handleCustomRange(CustomTimeframe.Month)}
                sx={{ height: ' 100%' }}
              >
                {CustomTimeframe.Month}
              </Button>
            </ButtonGroup>
          </LayoutGridItem>
        </LayoutGridItem>
        <Divider className={classes.divider} variant="middle" />
      </LayoutGrid>
    </>
  );
}
